import { BTLocalStorage } from "types/btStorage";

import type { ISearchHit } from "commonComponents/utilities/MainNavigation/SearchDialog/search/search.types";

/**
 * A record the user opened from the dialog, holding enough of the hit to route
 * and render it again with no fetch.
 *
 * Not the legacy `bt-objectArray-globalSearchRecentlyViewed` shape: that is
 * keyed on the `SearchCategory` enum, which `getEntityLink` cannot route.
 */
export interface ISearchDialogRecent {
    entity: string;
    id: string;
    title: string;
    subtitle: string | null;
    payload: Record<string, unknown> | null;
    /** Scopes the list so a shared machine doesn't leak one user's history. */
    userId: number;
    /** Epoch ms, newest first. */
    viewedAt: number;
}

/** Matches the legacy recents cap: useful, still short enough to scan. */
const MAX_RECENTS = 7;

function recentKey(entity: string, id: string): string {
    return `${entity.toLowerCase()}:${id}`;
}

export function getRecentItems(userId: number): ISearchDialogRecent[] {
    return BTLocalStorage.get("bt-objectArray-searchDialogRecents")
        .filter((item) => item.userId === userId)
        .sort((a, b) => b.viewedAt - a.viewedAt);
}

/**
 * The user's recents as the search service's `recent_refs`: `entity:id`,
 * newest first. The service ranks these records a little higher.
 */
export function getRecentRefs(userId: number): string[] {
    return getRecentItems(userId).map((item) => `${item.entity}:${item.id}`);
}

/**
 * Records an opened record, newest first, deduped on entity+id. Other users'
 * entries survive, since the cap is per user.
 */
export function addRecentItem(
    userId: number,
    hit: ISearchHit,
    display: { title: string; subtitle: string | null },
): void {
    const stored = BTLocalStorage.get("bt-objectArray-searchDialogRecents");
    const key = recentKey(hit.entity, hit.id);

    const mine = stored
        .filter((item) => item.userId === userId && recentKey(item.entity, item.id) !== key)
        .sort((a, b) => b.viewedAt - a.viewedAt);
    const others = stored.filter((item) => item.userId !== userId);

    const entry: ISearchDialogRecent = {
        entity: hit.entity,
        id: hit.id,
        title: display.title,
        subtitle: display.subtitle,
        payload: hit.payload ?? null,
        userId,
        viewedAt: Date.now(),
    };

    BTLocalStorage.set("bt-objectArray-searchDialogRecents", [
        ...others,
        entry,
        ...mine.slice(0, MAX_RECENTS - 1),
    ]);
}

/** The hit shape a stored recent stands in for, so it routes like a live one. */
export function recentToHit(item: ISearchDialogRecent): ISearchHit {
    return {
        entity: item.entity,
        id: item.id,
        score: 0,
        rerank_score: 0,
        reranker: "recent",
        summary: item.title,
        payload: item.payload ?? undefined,
    };
}
